import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { changePassword } from "@/api/auth";
import { useAuth } from "@/context/AuthContext";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setErr("");
    setOk("");

    if (newPassword.length < 8) {
      setErr("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (newPassword !== confirm) {
      setErr("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }

    try {
      setLoading(true);

      // Appel API centralisé : page -> api/auth.js -> lib/api.js -> FastAPI.
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Recharge le profil pour récupérer mustChangePassword=false.
      if (refresh) {
        await refresh();
      }

      setOk("Mot de passe modifié. Redirection vers votre espace...");
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (error) {
      setErr(
        error?.response?.data?.detail ||
        "Impossible de modifier le mot de passe."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen aurora-mesh flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-28 -left-28 w-80 h-80 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
        <div className="absolute bottom-[-120px] right-[-80px] w-96 h-96 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="rounded-[28px] border border-[var(--do-border)] bg-[var(--do-bg-elev)] backdrop-blur-xl shadow-2xl shadow-[var(--do-shadow-blue)] p-7 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] flex items-center justify-center shadow-lg shadow-[var(--do-shadow-blue)]">
              <LockKeyhole size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--do-accent)] font-bold">
                AlmaCRM
              </p>
              <h1 className="text-2xl font-bold text-[var(--do-ink)]">
                Changement obligatoire
              </h1>
            </div>
          </div>

          <p className="text-sm text-[var(--do-muted)] mb-6 leading-6">
            Votre compte a été créé avec un mot de passe temporaire.
            Pour continuer, choisissez un mot de passe personnel.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                Mot de passe temporaire
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                Confirmer le nouveau mot de passe
              </label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              />
            </div>

            {err && (
              <div className="flex gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-accent)]">
                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            {ok && (
              <div className="flex gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-accent)]">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                <span>{ok}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-[var(--do-accent-strong)] px-5 py-3.5 text-[var(--do-on-accent)] font-bold shadow-lg shadow-[var(--do-shadow-blue)] hover:bg-[var(--do-accent-strong)] disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {loading ? "Modification en cours..." : "Modifier mon mot de passe"}
            </button>
          </form>

          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[var(--do-bg-solid)] border border-[var(--do-border)] px-4 py-3">
            <ShieldCheck size={18} className="text-[var(--do-accent)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--do-muted)] leading-5">
              Après cette étape, votre accès complet à l'application sera réactivé.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
